'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { type Control, type FieldPath, type FieldValues, useController } from 'react-hook-form';

import { cn } from '@/lib/utils';

import { Input } from './input';

export type AddressData = {
    address?: string;
    zipCode?: string;
    city?: string;
};

export type InputAddressAutocompleteProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
    control: Control<TFieldValues>;
    name: TName;
    useAutocomplete?: boolean;
    placeholder?: string;
    className?: string;
    containerClassName?: string;
    label?: string;
    placeholderStreet?: string;
    placeholderZipCode?: string;
    placeholderCity?: string;
    disabled?: boolean;
    baseUrl?: string;
    minLength?: number;
    onAddressSelect?: (data: AddressData) => void;
};

type DAWAAddress = {
    tekst: string;
    caretpos: number;
    data: {
        href?: string;
        vejnavn?: string;
        husnr?: string;
        postnr?: string;
        postnrnavn?: string;
    };
};

const InputAddressAutocomplete = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    control,
    name,
    useAutocomplete = true,
    placeholder = 'Søg efter adresse...',
    className,
    containerClassName,
    label,
    placeholderStreet = 'Adresse',
    placeholderZipCode = 'Postnummer',
    placeholderCity = 'By',
    disabled = false,
    baseUrl = 'https://dawa.aws.dk',
    minLength = 2,
    onAddressSelect,
}: InputAddressAutocompleteProps<TFieldValues, TName>) => {
    const {
        field,
        fieldState: { error },
    } = useController({ control, name });

    const [isAutocompleteAvailable, setIsAutocompleteAvailable] = useState(true);
    const [searchValue, setSearchValue] = useState('');
    const [suggestions, setSuggestions] = useState<DAWAAddress[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [isLoading, setIsLoading] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);
    const suggestionsRef = useRef<HTMLDivElement>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Fallback to manual mode if autocomplete is not available
    const manualMode = !isAutocompleteAvailable || !useAutocomplete;

    const fetchAddresses = useCallback(
        async (query: string) => {
            if (query.length < minLength) {
                setSuggestions([]);
                return;
            }

            // Cancel previous request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            abortControllerRef.current = new AbortController();
            setIsLoading(true);

            try {
                const params = new URLSearchParams({
                    q: query,
                    type: 'adresse',
                    caretpos: query.length.toString(),
                    supplerendebynavn: 'true',
                    stormodtagerpostnumre: 'true',
                    fuzzy: 'true',
                });

                const response = await fetch(`${baseUrl}/autocomplete?${params}`, {
                    signal: abortControllerRef.current.signal,
                });

                if (!response.ok) {
                    throw new Error('DAWA API request failed');
                }

                const data = await response.json();
                setSuggestions(data || []);
                setShowSuggestions(true);
            } catch (err: unknown) {
                if (err instanceof Error && err.name !== 'AbortError') {
                    console.error('DAWA autocomplete error:', err);
                    // Fallback to manual mode on error
                    setIsAutocompleteAvailable(false);
                    setSuggestions([]);
                }
            } finally {
                setIsLoading(false);
            }
        },
        [baseUrl, minLength],
    );

    const debouncedFetch = useCallback(
        (query: string) => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            debounceTimerRef.current = setTimeout(() => {
                fetchAddresses(query);
            }, 300);
        },
        [fetchAddresses],
    );

    const handleSelectAddress = useCallback(
        (address: DAWAAddress) => {
            const addressData: AddressData = {
                address:
                    address.data?.vejnavn && address.data?.husnr
                        ? `${address.data.vejnavn} ${address.data.husnr}`
                        : undefined,
                zipCode: address.data?.postnr,
                city: address.data?.postnrnavn,
            };

            // Check if all required information is present
            const hasAllRequiredInfo = addressData.address && addressData.zipCode && addressData.city;

            setSearchValue(address.tekst);

            if (hasAllRequiredInfo) {
                // All information is present, complete the selection
                field.onChange(addressData);
                setShowSuggestions(false);
                setSuggestions([]);
                setSelectedIndex(-1);

                if (onAddressSelect) {
                    onAddressSelect(addressData);
                }
            } else {
                // Missing information, fetch more suggestions with updated input
                debouncedFetch(address.tekst);
            }

            // Focus back on input and set caret position from DAWA
            requestAnimationFrame(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                    const caretPosition = address.caretpos || address.tekst.length;
                    inputRef.current.setSelectionRange(caretPosition, caretPosition);
                }
            });
        },
        [field, onAddressSelect, debouncedFetch],
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (suggestions.length === 0) return;

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setShowSuggestions(true);
                    setSelectedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev));
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setShowSuggestions(true);
                    setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
                    break;
                case 'Enter':
                    e.preventDefault();
                    if (selectedIndex >= 0 && suggestions[selectedIndex]) {
                        handleSelectAddress(suggestions[selectedIndex]);
                    }
                    break;
                case 'Escape':
                    setShowSuggestions(false);
                    setSelectedIndex(-1);
                    break;
            }
        },
        [suggestions, selectedIndex, handleSelectAddress],
    );

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                suggestionsRef.current &&
                !suggestionsRef.current.contains(event.target as Node) &&
                inputRef.current &&
                !inputRef.current.contains(event.target as Node)
            ) {
                setShowSuggestions(false);
                setSelectedIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    // Auto-scroll selected item into view
    useEffect(() => {
        if (selectedIndex >= 0 && suggestionsRef.current) {
            const selectedElement = suggestionsRef.current.children[selectedIndex] as HTMLElement;
            if (selectedElement) {
                selectedElement.scrollIntoView({
                    block: 'nearest',
                    behavior: 'smooth',
                });
            }
        }
    }, [selectedIndex]);

    // Sync searchValue with field value in autocomplete mode
    useEffect(() => {
        if (!manualMode && field.value) {
            const currentValue = field.value as AddressData;
            if (currentValue.address || currentValue.zipCode || currentValue.city) {
                const displayValue = [currentValue.address, currentValue.zipCode, currentValue.city]
                    .filter(Boolean)
                    .join(', ');
                if (displayValue && displayValue !== searchValue) {
                    setSearchValue(displayValue);
                }
            }
        }
    }, [field.value, manualMode, searchValue]);

    const handleManualChange = useCallback(
        (updates: Partial<AddressData>) => {
            const currentValue = (field.value as AddressData) || { address: '', zipCode: '', city: '' };
            const newValue = {
                ...currentValue,
                ...updates,
            };
            field.onChange(newValue);
            if (onAddressSelect) {
                onAddressSelect(newValue);
            }
        },
        [field, onAddressSelect],
    );

    if (manualMode) {
        const currentValue = (field.value as AddressData) || { address: '', zipCode: '', city: '' };

        return (
            <>
                <div className={cn('space-y-4', containerClassName)}>
                    <Input
                        type='text'
                        placeholder={placeholderStreet}
                        className={className}
                        value={currentValue.address || ''}
                        onChange={e => handleManualChange({ address: e.target.value })}
                        disabled={disabled}
                        error={error}
                    />
                    <div className='grid grid-cols-2 gap-4'>
                        <Input
                            type='text'
                            placeholder={placeholderZipCode}
                            className={className}
                            value={currentValue.zipCode || ''}
                            onChange={e => handleManualChange({ zipCode: e.target.value })}
                            disabled={disabled}
                            error={error}
                        />
                        <Input
                            type='text'
                            placeholder={placeholderCity}
                            className={className}
                            value={currentValue.city || ''}
                            onChange={e => handleManualChange({ city: e.target.value })}
                            disabled={disabled}
                            error={error}
                        />
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <div className={cn('relative', containerClassName)}>
                <Input
                    id={name}
                    ref={inputRef}
                    type='text'
                    label={label}
                    placeholder={placeholder}
                    className={className}
                    value={searchValue}
                    onChange={e => {
                        const newValue = e.target.value;
                        setSearchValue(newValue);

                        // Clear the structured address data when user starts typing
                        if (field.value && typeof field.value === 'object') {
                            field.onChange(undefined);
                        }

                        debouncedFetch(newValue);
                    }}
                    onKeyDown={handleKeyDown}
                    onFocus={() => {
                        if (suggestions.length > 0) {
                            setShowSuggestions(true);
                        }
                    }}
                    disabled={disabled}
                    error={error}
                    autoComplete='off'
                />

                {showSuggestions && suggestions.length > 0 && (
                    <div
                        ref={suggestionsRef}
                        className='absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-xs bg-white shadow-lg'>
                        {suggestions.map((suggestion, index) => (
                            <div
                                key={
                                    suggestion.data?.href ||
                                    `${suggestion.data?.vejnavn}-${suggestion.data?.husnr}-${suggestion.data?.postnr}-${index}`
                                }
                                className={cn('text-body-md-400 cursor-pointer px-4 py-3')}
                                onClick={() => handleSelectAddress(suggestion)}
                                onMouseEnter={() => setSelectedIndex(index)}
                                onMouseMove={() => setSelectedIndex(index)}>
                                <div className={cn('text-black', { 'font-bold': index === selectedIndex })}>
                                    {suggestion.tekst}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {isLoading && (
                    <div className='absolute top-1/2 right-3 -translate-y-1/2'>
                        <div className='border-stroke-subtle border-t-stroke-focus h-5 w-5 animate-spin rounded-full border-2' />
                    </div>
                )}
            </div>
        </>
    );
};

InputAddressAutocomplete.displayName = 'InputAddressAutocomplete';

export { InputAddressAutocomplete };
